import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { ShieldCheck, FileText, CreditCard, Download, ArrowRight, CheckCircle2 } from "lucide-react";
import logo from "@/assets/logo.jpg";

const steps = [
  { icon: FileText, title: "Complete the form", text: "Work through a guided, step-by-step will instruction form at your own pace." },
  { icon: CreditCard, title: "Pay securely", text: "Settle your fee online via Stripe — Single Will £240 or Mirror Will £360 (inc. VAT)." },
  { icon: Download, title: "Submit & download", text: "Receive email confirmation and download a PDF copy of your instruction instantly." },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="App flex flex-col min-h-screen bg-brand-bg grain">
      <SiteHeader />
      <main className="flex-1">
        <section className="max-w-6xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-16 grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <img src={logo} alt="Rehoboth Wealth Group" className="h-20 md:h-24 w-auto object-contain mb-6" data-testid="hero-logo" />
            <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest text-brand-gold uppercase mb-5">
              <ShieldCheck className="w-4 h-4" /> Estate Planning · Rehoboth Wealth Group
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-green leading-[1.05] tracking-tight">
              Put your wishes on record with clarity and care.
            </h1>
            <p className="text-base md:text-lg text-brand-muted mt-6 leading-relaxed max-w-lg">
              Complete your Will Instruction Form online, pay securely, and receive a PDF copy the moment
              you submit. Track every step from your personal dashboard.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button size="lg" onClick={() => navigate("/register")} data-testid="hero-start-btn"
                className="bg-brand-green hover:bg-brand-greenHover text-white rounded-full px-7">
                Start your will <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/guide-to-service")} data-testid="hero-guide-btn"
                className="rounded-full px-7 border-brand-green text-brand-green hover:bg-brand-admin">
                Guide to the service
              </Button>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-sm text-brand-muted">
              {["Secure Stripe payment", "Instant PDF copy", "Email confirmation"].map((t) => (
                <span key={t} className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-brand-success" /> {t}</span>
              ))}
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="absolute -inset-4 bg-brand-gold/10 rounded-3xl -z-0" />
            <img src="https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200"
              alt="Fountain pen on paper" className="relative rounded-2xl border border-brand-line shadow-xl object-cover w-full h-[420px]" />
          </div>
        </section>

        <section className="bg-white border-y border-brand-line">
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-16">
            <h2 className="font-serif text-2xl md:text-3xl text-brand-green text-center">How it works</h2>
            <div className="grid md:grid-cols-3 gap-6 mt-10">
              {steps.map((s, i) => (
                <div key={s.title} className="rounded-xl border border-brand-line p-7 bg-brand-bg transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                  <div className="w-12 h-12 rounded-full bg-brand-green text-white flex items-center justify-center mb-4">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <div className="text-xs font-semibold text-brand-gold mb-1">STEP {i + 1}</div>
                  <h3 className="font-serif text-xl text-brand-green">{s.title}</h3>
                  <p className="text-sm text-brand-muted mt-2 leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-5 md:px-8 py-16 text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-brand-green">Ready to begin?</h2>
          <p className="text-brand-muted mt-3">Create your secure account and start your will instruction today.</p>
          <Button size="lg" onClick={() => navigate("/register")} data-testid="cta-register-btn"
            className="mt-6 bg-brand-green hover:bg-brand-greenHover text-white rounded-full px-8">
            Create an account <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-sm text-brand-muted mt-4">Already registered? <Link to="/login" className="text-brand-green font-semibold underline">Sign in</Link></p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
